import React, { useState, useEffect } from 'react';
import Grid from '@mui/material/Grid2';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Tooltip from '@mui/material/Tooltip';
import TextFieldComponent from '../components/TextFieldComponent';
import Button from '@mui/material/Button';
import { useDispatch, useSelector } from 'react-redux';
import { isSuperUser } from '../redux/selectors/userSelector';
import { ArrowBackIosNew } from '@mui/icons-material';
import HeaderComponent from '../components/HeaderComponent';
import PaginationComponent from '../components/PaginationComponent';
import StickyTableContainer from '../components/StickyTableContainer';
import { deleteSubscriptionPlanRecord, getSubscriptionPlansList } from '../redux/actions/commonActions';
import AddSubscriptionPlan from '../models/AddSubscriptionPlan';
import ConfirmationDialog from '../components/ConfirmationDialog';


const ViewSubscriptionPlans = () => {

  const dispatch = useDispatch();
  const subscriptionPlans = useSelector((state) => state.common.subscriptionPlans);

  const totalRecords = subscriptionPlans?.metadata?.totalRecords;
  const totalPages = subscriptionPlans?.metadata?.totalPages;

  const [isAddSubscriptionPlanVisible, setIsAddSubscriptionPlanVisible] = useState(false);
  const [subscriptionPlanDetail, setSubscriptionPlanDetail] = useState(null);
  const [isSubscriptionPlanDeleteModalOpen, setIsSubscriptionPlanDeleteModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [typingTimeout, setTypingTimeout] = useState(null);

  const [queryParams, setQueryParams] = useState({
    pageNumber: 0, // default to the first page
    searchText: '',
    sort: ''
  });

  const handlePageChange = (event, page) => {
    setQueryParams((prevParams) => ({ ...prevParams, pageNumber: page - 1 }));
  };

  useEffect(() => {
    dispatch(getSubscriptionPlansList(queryParams));
  }, [dispatch, queryParams]);

  const handleSearch = (text) => {
    setSearchTerm(text);
    clearTimeout(typingTimeout); // Clear the previous timeout
    const timeout = setTimeout(() => {
      setQueryParams((prevParams) => ({ ...prevParams, searchText: text, pageNumber: 0 }));
    }, 700);
    setTypingTimeout(timeout);
  };

  const onNewSubscriptionPlanAdded = () => {
    dispatch(getSubscriptionPlansList(queryParams));
  }

  const handleAddSubscriptionPlan = () => {
    setSubscriptionPlanDetail(null);
    setIsAddSubscriptionPlanVisible(!isAddSubscriptionPlanVisible);
  }

  const handleEditClick = (subPlan) => {
    setSubscriptionPlanDetail(subPlan);
    setIsAddSubscriptionPlanVisible(true);
  }

  const handleDeleteClick = (subPlan) => {
    setSubscriptionPlanDetail(subPlan);
    setIsSubscriptionPlanDeleteModalOpen(true);
  }

  const handleConfirmationDialogClose = () => {
    setIsSubscriptionPlanDeleteModalOpen(false);
    setSubscriptionPlanDetail(null);
  };

  const handleConfirmationDialog = () => {
    setIsSubscriptionPlanDeleteModalOpen(false);
    dispatch(deleteSubscriptionPlanRecord(subscriptionPlanDetail.id));
    setSubscriptionPlanDetail(null);
  };

  const superUser = useSelector(isSuperUser);

  return (
    <>
      <HeaderComponent name="Subscription Plans" totalRecords={totalRecords} />
      <Grid container spacing={3}>
        {/* Left side: Admins Table */}
        <Grid size={{ xs: 12, md: isAddSubscriptionPlanVisible ? 8 : 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {/* Search Input on the Left Side */}
            <TextFieldComponent
              label="Search"
              type="search"
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              style={{ width: '350px' }}
            />

            {/* Add User Button on the Right Side */}
            {superUser && <Tooltip title="Add User">
              <Button style={{ whiteSpace: 'nowrap' }} variant="contained" color="primary" onClick={handleAddSubscriptionPlan}>
                Add Plan
              </Button>
            </Tooltip>}
          </div>

          <div style={{ margin: '16px 0' }} />

          <StickyTableContainer>
            <TableHead>
              <TableRow>
                <TableCell style={styles.textBold}>Plan Name</TableCell>
                <TableCell style={styles.textBold}>Description</TableCell>
                <TableCell style={styles.textBold}>Active Subscriptions</TableCell>
                <TableCell style={styles.textBold}>Total Months</TableCell>
                <TableCell style={styles.textBold}>No. of Job Posts</TableCell>
                <TableCell style={styles.textBold}>Price</TableCell>
                <TableCell style={styles.textBold}>Discount %</TableCell>
                <TableCell style={styles.textBold}>Active</TableCell>
                {superUser && <TableCell style={styles.textBold}>Edit</TableCell>}
                {superUser && <TableCell style={styles.textBold}>Delete</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {subscriptionPlans?.data?.map((subPlan, index) => (
                <TableRow key={index}>
                  <TableCell>{subPlan?.planName}</TableCell>
                  <TableCell sx={{ maxWidth: '400px' }}>{subPlan?.planDescription}</TableCell>
                  <TableCell>{subPlan?.activeSubscriptionsCount}</TableCell>
                  <TableCell>{subPlan?.totalMonths}</TableCell>
                  <TableCell>{subPlan?.noOfJobPosts}</TableCell>
                  <TableCell>{subPlan?.price}</TableCell>
                  <TableCell>{subPlan?.discount}</TableCell>
                  <TableCell>{subPlan?.active ? "Yes" : "No"}</TableCell>

                  {superUser && <TableCell>
                    <IconButton onClick={() => handleEditClick(subPlan)}>
                      <EditIcon />
                    </IconButton>
                  </TableCell>}
                  {superUser && <TableCell>
                    <IconButton onClick={() => handleDeleteClick(subPlan)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>}
                </TableRow>
              ))}
            </TableBody>
          </StickyTableContainer>

          <Grid container justifyContent="flex-end" style={{ marginTop: '16px' }}>
            <PaginationComponent
              pageNumber={queryParams.pageNumber + 1}
              totalPages={totalPages}
              handlePageChange={handlePageChange}
            />
          </Grid>
        </Grid>

        {/* Right side: Add Subscription Plan */}
        {isAddSubscriptionPlanVisible && (
          <Grid size={{ xs: 12, md: 4 }} style={{ paddingTop: '16px', paddingLeft: '16px', background: 'white', borderRadius: '5px 0', boxShadow: '-5px -5px 5px rgb(0 0 0 / 0.2)' }}>
            <Button variant='contained' startIcon={<ArrowBackIosNew />} onClick={handleAddSubscriptionPlan} style={{ marginBottom: '16px' }}>
              Back
            </Button>
            <AddSubscriptionPlan onClose={() => setIsAddSubscriptionPlanVisible(false)} subscriptionPlanDetail={subscriptionPlanDetail} onNewSubscriptionPlanAdded={onNewSubscriptionPlanAdded} />
          </Grid>
        )}

        {isSubscriptionPlanDeleteModalOpen === true && (
          <ConfirmationDialog
            open={true}
            onClose={handleConfirmationDialogClose}
            onConfirm={handleConfirmationDialog}
            message="Do you want to Delete?"
          />
        )}

      </Grid>
    </>
  );
};

export default ViewSubscriptionPlans;

const styles = {
  textBold: {
    fontWeight: 'bold'
  }
}